import React, { useEffect, useState, useRef } from 'react';
import { useCampaignOrderContext } from '../../../../../core/components/campaingOrder/useCampaignOrderContext';
import { Modal, Row, Col, Button, Table, Select, DatePicker, Input, Tag, Alert, Checkbox } from 'antd';
import { PageTitleText } from '../../../../shared/globalStyling/styledText';
import { FilterOutlined, CloseCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import { debounce } from 'lodash';
import { campaignStatusOptions } from '../../../../../core/utils/constants/constants';
import { formatCampaignName, hasSpecialCharacters } from '../../../../../core/utils/campaignNaming';
import useResponsiveTask from '../../hooks/useResponsiveTask';
import { space } from '../../configs/designTokens';
import '../../styles/index.scss';

const { RangePicker } = DatePicker;
const { Option } = Select;

const columns = [
  {
    title: 'Campaign Name',
    dataIndex: 'name',
    key: 'name',
    width: '50%',
    render: (text, record) => {
      const displayName = !hasSpecialCharacters(text)
        ? formatCampaignName(record)
        : text;
      return (
        <a
          href={`/campaigns/details/${record.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="task-related-campaigns-modal__campaign-link"
          onClick={(e) => e.stopPropagation()}
        >
          {displayName}
        </a>
      );
    },
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    render: (status) => {
      const statusOption = campaignStatusOptions.find((option) => option.value === status);
      const statusLabel = statusOption ? statusOption.label : status;
      return <Tag color={getStatusColor(status)}>{statusLabel}</Tag>;
    },
  },
  {
    title: 'Start Date',
    dataIndex: 'startDate',
    key: 'startDate',
    render: (startDate) => {
      return moment(startDate).format('MM/DD/YYYY');
    },
  },
  {
    title: 'End Date',
    dataIndex: 'endDate',
    key: 'endDate',
    render: (endDate) => {
      return moment(endDate).format('MM/DD/YYYY');
    },
  },
];

const getStatusColor = (status) => {
  const statusColorMap = {
    DRAFT: 'default',
    PENDING: 'orange',
    APPROVED: 'blue',
    ACTIVE: 'green',
    PAUSED: 'gold',
    COMPLETED: 'purple',
    CANCELLED: 'red',
    REJECTED: 'red',
  };
  return statusColorMap[status] || 'default';
};

const TaskRelatedCampaignsModal = ({
  level,
  visible,
  setVisible,
  channelPartnerOrgId,
  advertiserOrgId,
  onAddSelections,
  selectedCampaigns,
}) => {
  const {
    fetchBasicCampaignOrdersDetailsConnection,
    campaignOrders,
    basicCampaignOrdersDetailsLoading,
    campaignOrdersPagination,
    setCampaignOrdersPagination,
  } = useCampaignOrderContext();

  const { width: windowWidth, isMobile, isDesktop } = useResponsiveTask();

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [combinedSelectedCampaigns, setCombinedSelectedCampaigns] = useState([]);

  // Filter states
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDateRange, setFilterDateRange] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [bypassFiltering, setBypassFiltering] = useState(false);

  const debouncedFetchRef = useRef(
    debounce(({ status = '', dateRange = null, search = '', page = 1, pageSize = 10, bypass = false }) => {
      let dateStart = null;
      let dateEnd = null;
      if (dateRange && dateRange.split(',').length === 2) {
        [dateStart, dateEnd] = dateRange.split(',');
      }

      const whereConditions = {
        AND: [],
      };

      if (channelPartnerOrgId && !bypass) {
        whereConditions.AND.push({
          orgs_some: {
            OR: [
              {
                parentOrg_some: {
                  id: channelPartnerOrgId,
                },
              },
              {
                id: channelPartnerOrgId,
              },
            ],
          },
        });
      }

      if (advertiserOrgId && typeof advertiserOrgId === 'string' && !bypass) {
        whereConditions.AND.push({
          orgs_some: {
            OR: [
              {
                parentOrg_some: {
                  id: advertiserOrgId,
                },
              },
              {
                id: advertiserOrgId,
              },
            ],
          },
        });
      }

      if (status) {
        whereConditions.AND.push({ status });
      }

      if (dateStart && dateEnd) {
        whereConditions.AND.push({
          startDate_gte: moment(dateStart).startOf('day').toISOString(),
          endDate_lte: moment(dateEnd).endOf('day').toISOString(),
        });
      }

      if (search) {
        whereConditions.AND.push({
          name_contains: search,
        });
      }

      const skip = (page - 1) * pageSize;

      fetchBasicCampaignOrdersDetailsConnection({
        variables: {
          where: whereConditions,
          orderBy: 'createdAt_DESC',
          first: pageSize,
          skip: skip,
        },
      });
    }, 500)
  ).current;

  const handleAddSelections = () => {
    // Filter and update only the currently selected campaigns
    const newSelectedItems = campaignOrders.filter((item) => selectedRowKeys.includes(item.id));
    // Prevent duplicates by combining with existing selected campaigns
    const updatedSelectedCampaigns = combinedSelectedCampaigns.filter(
      (campaign) => selectedRowKeys.includes(campaign.id) // Keep only still selected campaigns
    );

    newSelectedItems.forEach((item) => {
      if (!updatedSelectedCampaigns.some((campaign) => campaign.id === item.id)) {
        updatedSelectedCampaigns.push(item);
      }
    });

    onAddSelections(updatedSelectedCampaigns);
    setVisible(false);
  };

  const handleRowSelect = (record, selected) => {
    if (selected) {
      setSelectedRowKeys((prev) => [...prev, record.id]);
      if (!combinedSelectedCampaigns.some((c) => c.id === record.id)) {
        setCombinedSelectedCampaigns((prev) => [...prev, record]);
      }
    } else {
      setSelectedRowKeys((prev) => prev.filter((key) => key !== record.id));
      setCombinedSelectedCampaigns((prev) => prev.filter((c) => c.id !== record.id));
    }
  };

  const handleSelectAll = (selected, selectedRows, changeRows) => {
    if (selected) {
      const newKeys = changeRows.map((row) => row.id);
      setSelectedRowKeys((prev) => [...new Set([...prev, ...newKeys])]);

      const newCampaigns = changeRows.filter(
        (row) => !combinedSelectedCampaigns.some((c) => c.id === row.id)
      );
      setCombinedSelectedCampaigns((prev) => [...prev, ...newCampaigns]);
    } else {
      const keysToRemove = changeRows.map((row) => row.id);
      setSelectedRowKeys((prev) => prev.filter((key) => !keysToRemove.includes(key)));
      setCombinedSelectedCampaigns((prev) => prev.filter((c) => !keysToRemove.includes(c.id)));
    }
  };

  const handleFetchCampaignOrders = (page = null, pageSize = null) => {
    const dateRange =
      filterDateRange && filterDateRange[0] && filterDateRange[1]
        ? `${filterDateRange[0].format('YYYY-MM-DD')},${filterDateRange[1].format('YYYY-MM-DD')}`
        : null;

    const currentPage = page !== null ? page : campaignOrdersPagination.current || 1;
    const currentPageSize = pageSize !== null ? pageSize : campaignOrdersPagination.pageSize || 10;

    debouncedFetchRef({
      status: filterStatus,
      dateRange,
      search: searchQuery,
      page: currentPage,
      pageSize: currentPageSize,
      bypass: bypassFiltering && level === 'PLATFORM',
    });
  };

  const handleFilterChange = (filterType, value) => {
    switch (filterType) {
      case 'status':
        setFilterStatus(value);
        break;
      case 'dateRange':
        setFilterDateRange(value);
        break;
      case 'search':
        setSearchQuery(value);
        break;
      default:
        break;
    }
  };

  const handleClearFilters = () => {
    setFilterStatus('');
    setFilterDateRange(null);
    setSearchQuery('');
  };

  useEffect(() => {
    if (visible) {
      if (selectedCampaigns && selectedCampaigns.length > 0) {
        setCombinedSelectedCampaigns(selectedCampaigns);
        setSelectedRowKeys(selectedCampaigns.map(c => c.id));
      } else {
        setCombinedSelectedCampaigns([]);
        setSelectedRowKeys([]);
      }
      handleFetchCampaignOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    if (visible) {
      setCampaignOrdersPagination((prev) => ({ ...prev, current: 1 }));
      handleFetchCampaignOrders(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterDateRange, searchQuery, bypassFiltering]);

  const rowSelection = {
    selectedRowKeys,
    onSelect: handleRowSelect,
    onSelectAll: handleSelectAll,
  };

  const hasActiveFilters = filterStatus || filterDateRange || searchQuery;

  return (
    <div>
      <Modal
        className="task-related-campaigns-modal"
        visible={visible}
        width={isMobile ? '95%' : '70%'}
        closable={false}
        onCancel={() => setVisible(false)}
        okButtonProps={{ style: { display: 'none' } }}
        bodyStyle={{ padding: isMobile ? space[3] : space[6] }}
      >
        <Row style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          marginBottom: space[5],
          gap: isMobile ? space[3] : space[0]
        }}>
          <PageTitleText text={'ATTACH CAMPAIGNS'} />
          <Button
            type="primary"
            onClick={() => handleAddSelections()}
            style={{ width: isMobile ? '100%' : 'auto' }}
          >
            Attach Campaign(s)
          </Button>
        </Row>

        <Row className="task-related-campaigns-modal__alert-row">
          <Col span={24}>
            <Alert
              closable
              message="Campaign Filtering"
              description={
                bypassFiltering
                  ? "All available campaigns are shown below. Campaign filtering is currently bypassed."
                  : channelPartnerOrgId && advertiserOrgId
                    ? `Campaigns shown below are filtered by the ${level === 'PLATFORM' ? 'selected' : ' '} channel partner and advertiser.`
                    : channelPartnerOrgId
                      ? `Campaigns shown below are filtered by the ${level === 'PLATFORM' ? 'selected' : ' '} channel partner. Select an advertiser to further narrow results.`
                      : "All available campaigns are shown below. To filter campaigns, please select a channel partner from the task form."
              }
              type="info"
              icon={<InfoCircleOutlined />}
              showIcon
            />
          </Col>
          {level === 'PLATFORM' && (channelPartnerOrgId || advertiserOrgId) && (
            <Col span={24} className="task-related-campaigns-modal__bypass-row">
              <Checkbox
                checked={bypassFiltering}
                onChange={(e) => setBypassFiltering(e.target.checked)}
              >
                <span className="task-related-campaigns-modal__bypass-label">Bypass Campaign Filtering</span>
                <span className="task-related-campaigns-modal__bypass-hint">
                  (Show all campaigns regardless of channel partner or advertiser)
                </span>
              </Checkbox>
            </Col>
          )}
        </Row>

        <Row gutter={[16, 16]} className="task-related-campaigns-modal__filters">
          <Col xs={24} sm={12} md={8}>
            <Select
              placeholder="Status"
              value={filterStatus || undefined}
              onChange={(value) => handleFilterChange('status', value)}
              allowClear
              className="task-w--full"
              suffixIcon={<FilterOutlined />}
            >
              {campaignStatusOptions.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <RangePicker
              value={filterDateRange}
              onChange={(dates) => handleFilterChange('dateRange', dates)}
              format="YYYY-MM-DD"
              className="task-w--full"
              placeholder={isMobile ? ['Start', 'End'] : ['Start Date', 'End Date']}
              popupStyle={{
                maxWidth: isMobile ? '90vw' : 'auto',
                left: isMobile ? '50%' : undefined,
                transform: isMobile ? 'translateX(-50%)' : undefined,
              }}
              dropdownClassName={isMobile ? 'task-mobile-range-picker-dropdown' : ''}
              getPopupContainer={isMobile ? (trigger) => trigger.parentElement : undefined}
            />
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Input.Search
              placeholder="Search by name"
              value={searchQuery}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              allowClear
              className="task-w--full"
            />
          </Col>

          {hasActiveFilters && (
            <Col xs={24}>
              <Button
                icon={<CloseCircleOutlined />}
                onClick={handleClearFilters}
                size="small"
                type="link"
              >
                Clear All Filters
              </Button>
            </Col>
          )}
        </Row>

        <Table
          rowKey="id"
          rowSelection={rowSelection}
          columns={columns}
          loading={basicCampaignOrdersDetailsLoading}
          dataSource={campaignOrders}
          pagination={{
            ...campaignOrdersPagination,
            size: isMobile ? 'small' : 'default',
            showSizeChanger: !isMobile,
          }}
          scroll={{ x: isMobile ? 800 : undefined }}
          size={isMobile ? 'small' : 'default'}
          onChange={(paginationObject) => {
            setCampaignOrdersPagination({
              ...campaignOrdersPagination,
              current: paginationObject.current,
              pageSize: paginationObject.pageSize,
            });
            handleFetchCampaignOrders(paginationObject.current, paginationObject.pageSize);
          }}
        />
      </Modal>
    </div>
  );
};

export default TaskRelatedCampaignsModal;
